import { apiFetch } from "@/lib/api";
import type { InventoryBox } from "@/lib/inventoryBoxApi";
import type { InventoryAmlMpnAlternate } from "@/lib/inventoryAmlMpn";

export type WarehouseHubLocation = {
  hub_location_id: number;
  location_name: string | null;
  megaventory_location_id: number | null;
};

export type WarehouseRecountLocationSummary = {
  hub_location_id: number;
  location_name: string | null;
  on_hand: number;
  boxed_total: number;
  transient_quantity: number;
  unclaimed: number;
  mpn_breakdown: WarehouseRecountMpnBreakdownRow[];
};

export type WarehouseRecountShelfSublocation = {
  sublocation: string;
  label: string;
  hub_quantity: number;
  labeled_quantity: number;
  carton_count: number;
  /** True when no cartons remain and this is the last shelf that held this part. */
  last_known?: boolean;
};

export type WarehouseRecountMpnContainerLine = {
  hex: string;
  short_code: string;
  quantity: number;
  unit_kind: string;
  sublocation_label: string;
  hide_carton_code?: boolean;
};

export type WarehouseRecountMpnBreakdownRow = {
  approved_component_id: string | null;
  mpn_label: string;
  in_cartons: number;
  on_hand_quantity: number;
  transient_quantity: number;
  carton_count: number;
  containers: WarehouseRecountMpnContainerLine[];
};

export type WarehouseRecountContext = {
  sku: string;
  hub_location_id: number | null;
  location_name: string | null;
  megaventory_location_id: number | null;
  on_hand: number;
  boxed_total: number;
  transient_quantity: number;
  unclaimed: number;
  mv_description: string;
  mv_variant: string;
  aml_mpn_alternates: InventoryAmlMpnAlternate[];
  sku_locations: WarehouseRecountLocationSummary[];
  shelf_sublocations: WarehouseRecountShelfSublocation[];
  mpn_breakdown: WarehouseRecountMpnBreakdownRow[];
  box: InventoryBox | null;
  box_badges: Array<{
    kind: "picked" | "prepack" | "to_stock" | "holding";
    label: string;
  }>;
};

export async function fetchWarehouseLocations(): Promise<WarehouseHubLocation[]> {
  const data = await apiFetch<{ locations: WarehouseHubLocation[] }>("/api/warehouse/locations");
  return data.locations;
}

export type WarehouseSkuSearchRow = {
  sku: string;
  description: string | null;
  variant: string | null;
  category: string | null;
};

export async function searchWarehouseSkus(
  query: string,
  signal?: AbortSignal,
): Promise<WarehouseSkuSearchRow[]> {
  const data = await apiFetch<{ rows: WarehouseSkuSearchRow[] }>("/api/warehouse/sku-search", {
    query: { q: query },
    signal,
  });
  return data.rows;
}

export async function fetchWarehouseRecountContext(input: {
  sku: string;
  hubLocationId?: number | null;
  boxHex?: string | null;
}): Promise<WarehouseRecountContext> {
  const params = new URLSearchParams({
    sku: input.sku,
  });
  if (input.hubLocationId != null && input.hubLocationId > 0) {
    params.set("hub_location_id", String(input.hubLocationId));
  }
  if (input.boxHex != null && input.boxHex.trim() !== "") {
    params.set("box_hex", input.boxHex.trim());
  }
  const data = await apiFetch<{ context: WarehouseRecountContext }>(
    `/api/warehouse/recount/context?${params.toString()}`,
  );
  return data.context;
}

export async function verifyWarehouseRecountMpn(
  sku: string,
  scannedMpn: string,
  manufacturerName?: string | null,
): Promise<
  | { status: "not_required" }
  | { status: "matched"; approved_component_id: number }
  | { status: "unmatched" }
  | { status: "choose_manufacturer"; manufacturers: string[] }
> {
  return apiFetch<
    | { status: "not_required" }
    | { status: "matched"; approved_component_id: number }
    | { status: "unmatched" }
    | { status: "choose_manufacturer"; manufacturers: string[] }
  >("/api/warehouse/recount/verify-mpn", {
    method: "POST",
    body: {
      sku,
      scanned_mpn: scannedMpn,
      manufacturer_name: manufacturerName ?? null,
    },
  });
}

export async function saveWarehouseBoxRecount(input: {
  boxHex: string;
  quantity: number;
  hubLocationId: number;
  sublocation?: string | null;
  unitKind?: string | null;
  approvedComponentId?: string | null;
  reason?: string | null;
}): Promise<{ box: InventoryBox; context: WarehouseRecountContext }> {
  const body: {
    box_hex: string;
    quantity: number;
    hub_location_id: number;
    sublocation?: string | null;
    unit_kind?: string;
    approved_component_id?: number;
    reason?: string;
  } = {
    box_hex: input.boxHex,
    quantity: input.quantity,
    hub_location_id: input.hubLocationId,
  };
  if (input.sublocation !== undefined) {
    body.sublocation = input.sublocation;
  }
  if (input.unitKind != null && input.unitKind.trim() !== "") {
    body.unit_kind = input.unitKind.trim();
  }
  if (input.approvedComponentId != null && input.approvedComponentId.trim() !== "") {
    body.approved_component_id = Number.parseInt(input.approvedComponentId, 10);
  }
  if (input.reason != null && input.reason.trim() !== "") {
    body.reason = input.reason.trim();
  }
  return apiFetch<{ box: InventoryBox; context: WarehouseRecountContext }>(
    "/api/warehouse/recount/box",
    { method: "POST", body },
  );
}

export async function carveWarehouseBox(input: {
  sku: string;
  hubLocationId: number;
  quantity: number;
  printer: string;
  sublocation?: string | null;
  unitKind: string;
  approvedComponentId?: string | null;
}): Promise<{
  box: InventoryBox;
  context: WarehouseRecountContext;
  inventory_discrepancy_request: { id: string; quantity: number } | null;
}> {
  const body: {
    sku: string;
    hub_location_id: number;
    quantity: number;
    printer: string;
    sublocation?: string;
    unit_kind: string;
    approved_component_id?: number;
  } = {
    sku: input.sku,
    hub_location_id: input.hubLocationId,
    quantity: input.quantity,
    printer: input.printer,
    unit_kind: input.unitKind,
  };
  if (input.sublocation != null && input.sublocation.trim() !== "") {
    body.sublocation = input.sublocation.trim();
  }
  if (input.approvedComponentId != null && input.approvedComponentId.trim() !== "") {
    body.approved_component_id = Number.parseInt(input.approvedComponentId, 10);
  }
  return apiFetch<{
    box: InventoryBox;
    context: WarehouseRecountContext;
    inventory_discrepancy_request: { id: string; quantity: number } | null;
  }>(
    "/api/warehouse/recount/carve",
    { method: "POST", body },
  );
}

export async function printWarehouseRecountLocationLabel(input: {
  sku: string;
  hubLocationId: number;
  printer: string;
}): Promise<{ printed: true; sku: string; location_id: number; scan_url: string }> {
  return apiFetch<{ printed: true; sku: string; location_id: number; scan_url: string }>(
    "/api/warehouse/recount/print-location",
    {
      method: "POST",
      body: {
        sku: input.sku,
        hub_location_id: input.hubLocationId,
        printer: input.printer,
      },
    },
  );
}

export function warehouseRecountDescriptionLine(context: WarehouseRecountContext): string {
  return [context.mv_description.trim(), context.mv_variant.trim()].filter((part) => part !== "").join(" ");
}
